<template>
  <div ref="productShowcase">
    <ErrorBoundary>
      <Heading
        v-if="title"
        :h-class="titleHClass || `h${titleLevel}`"
        :level="titleLevel"
        :title="title"
        :use-h-tag="useHTag"
        class="mb-3 md:mb-2 kft-product-showcase__heading kft-heading--left"
      />

      <ProductsLoader
        v-show="loading || !isProductsReady"
        :is-horizontal="isSlider"
        :products-count="products?.length"
      />

      <div v-show="!loading && isProductsReady" class="flex flex-col">
        <component
          :is="productShowcaseContainer"
          class="product-showcase-cards-container"
          :class="{
            'product-showcase-cards-container--column': isSingleColumn,
          }"
          :data-test-id="showcaseContainerDataTestId"
        >
          <template
            v-for="(item, i) in products"
            :key="`kft-search-product-card-${item.objectID}`"
          >
            <component
              :is="productCard"
              :class="{
                flex: !isSlider,
                'slider-item': isSlider,
                hidden: i + 1 > showItemsOnDesktop && isCollapsed,
              }"
            >
              <transition name="fade-slide-up">
                <!-- using v-show for animation effect on homepage -->
                <!-- probably can be fully removed with <transition> -->
                <ProductCard
                  v-show="
                    isMobile ||
                    !isCollapsed ||
                    (i < showItemsOnDesktop && isCollapsed)
                  "
                  v-bind="productCardProps(item, i)"
                  :is-slide="isSlider"
                  :before-navigation-callback="
                    () => fnHandleClickOnCard(item, i)
                  "
                  @click:wishlist="handleClickWishlist(item)"
                />
              </transition>
            </component>

            <ProductCard
              v-if="i === 0 && specialGiftcardEvent"
              v-bind="productGiftcardProps(specialGiftcardEvent)"
              :is-slide="isSlider"
              is-special-giftcard
              @click:wishlist="handleClickWishlist(specialGiftcardEvent)"
            />

            <template v-for="(position, index) in promotionSlotIndices">
              <slot v-if="i === position" :name="`promotion${index + 1}`" />
            </template>
          </template>
        </component>

        <Link
          v-if="(products?.length > 4 && isDesktopAccordeon) || buttonLink"
          :link="localePath(buttonLink)"
          class="show-more-button"
        >
          <Button class="w-full">
            {{ t('product.showcase.showMore') }}
          </Button>
        </Link>
      </div>
      <!-- End::Product List -->
    </ErrorBoundary>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, ref, onMounted, watch } from 'vue'
import { SwiperSlide } from 'swiper/vue'
import type { AlgoliaEventItem } from '~/composables/types/algolia'
import {
  sendViewItemList,
  sendSelectListItem,
} from '~/composables/useGtmEvents'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'
import { useSendEventToAlgolia } from '~/composables/product/useProduct'
import { useGetFormattedDuration } from '~/composables/useGetFormattedDuration'
import { useWishlistSidebarUiState } from '~/composables/useUiState'
import { useWishlist } from '~/composables/useWishlist'
import { useIntersectionObserver } from '~/composables/useIntersectionObserver'
import { algoliaEventGetters } from '~/utils/algoliaEventGetters'
import { convertLocaleIntoBCP47 } from '~/utils/localeHelpers'
import type { GtmItemListId } from '~/composables/useKftGtm/types'
import type { ProductCardImageBadge } from '~/components/product/ProductCardImage.vue'
import Link from '~/components/common/Link.vue'
import ProductsShowcaseSlider from '~/components/product/ProductsShowcaseSlider.vue'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const { token: userToken } = useAlgoliaSearchInsights()
const { sendViewedObjectIDs } = useAlgoliaConversion()

const { isMobile } = useDevice()
const {
  addAlgoliaEventItem,
  isAlgoliaEventItemOnWishlist,
  removeAlgoliaEventItem,
} = useWishlist()
const { toggle: toggleWishlistSidebar } = useWishlistSidebarUiState()

const emit = defineEmits(['click:product-card'])

interface Props {
  title?: string
  queryId?: string | string[]
  titleLevel?: number
  titleHClass?: string
  tags?: any[]
  products?: any[]
  imageLoading?: '' | 'lazy' | 'eager'
  promotionSlotIndices?: number[]
  loading?: boolean
  buttonLink?: string
  disableSendEventToAlgolia?: boolean
  algoliaInsightsLabel?: string
  algoliaIndexName?: string
  gtmItemListId?: string | null
  algoliaInsightsClient?: object | void | null
  giftCardEvent?: boolean
  showItemsOnDesktop?: number
  useHTag?: boolean
  specialGiftcardEvent?: AlgoliaEventItem
  isDesktopAccordeon?: boolean
  isMobileSlider?: boolean
  isDurationVisible?: boolean
  isSingleColumn?: boolean
  showcaseContainerDataTestId?: string
  refreshOnClientSide?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  queryId: '',
  titleLevel: 2,
  titleHClass: 'text-h3 font-semibold',
  tags: () => [],
  products: () => [],
  imageLoading: 'lazy',
  promotionSlotIndices: () => [1, 9],
  loading: false,
  buttonLink: '',
  disableSendEventToAlgolia: false,
  algoliaInsightsLabel: '',
  algoliaIndexName: undefined,
  gtmItemListId: null,
  algoliaInsightsClient: () => ({}),
  giftCardEvent: false,
  mobileHorizontalScroll: false,
  showItemsOnDesktop: 4, // works together with isDesktopAccordeon
  useHTag: true,
  specialGiftcardEvent: undefined,
  isDesktopAccordeon: false,
  isMobileSlider: false,
  isDurationVisible: true,
  isSingleColumn: false,
  showcaseContainerDataTestId: 'search-results',
  refreshOnClientSide: true,
})

// Dynamic slider components import
const isSlider = computed(() => props.isMobileSlider && isMobile)
const productShowcaseContainer = computed(() =>
  isSlider.value ? ProductsShowcaseSlider : 'div'
)
const productCard = computed(() => (isSlider.value ? SwiperSlide : 'div'))

const headers = useRequestHeaders(['x-bot'])

// Hide loading until components are mounted
// If refreshOnClientSide is false, products are ready immediately (no loader)
// Otherwise, show loader until mount (or if bot on server)
const isProductsReady = ref(
  props.refreshOnClientSide === false
    ? true
    : import.meta.client
      ? false
      : headers['x-bot'] === 'true'
)
onMounted(() => {
  // Only delay if refreshOnClientSide is true (default behavior)
  if (props.refreshOnClientSide !== false) {
    nextTick(() => {
      isProductsReady.value = true
    })
  }
})

const getBadges = (item: AlgoliaEventItem): ProductCardImageBadge[] => {
  const badges: ProductCardImageBadge[] = []

  const generalBadgeLabel = algoliaEventGetters.getBadgeLabel(item, t)

  if (generalBadgeLabel)
    badges.push({
      label: generalBadgeLabel,
      variant: 'default' as const,
    })

  if (algoliaEventGetters.getHasPromotions(item))
    badges.push({ label: t('product.card.sale'), variant: 'promo' as const })

  return badges
}

// Product card props in one place
const productCardProps = (item: AlgoliaEventItem, index: number) => {
  return {
    dataInsightsObjectId: item.objectID,
    duration: fnGetFormattedDuration(algoliaEventGetters.fnGetDuration(item)),
    durationTitle: t('product.card.duration'),
    minNumberOfPeople: item.min_number_of_people ?? 0,
    maxNumberOfPeople: item.max_number_of_people ?? 0,
    image: algoliaEventGetters.getPrincipalImage(item),
    imageLoading: props.imageLoading,
    next: null,
    link: algoliaEventGetters.fnGetLink(item),
    productCategory: algoliaEventGetters.fnGetMainCategory(item),
    productType: '',
    badges: getBadges(item),
    rating: algoliaEventGetters.getAverageRating(item, localeBCP47),
    isPartnerRating: algoliaEventGetters.getIsPartnerRating(item),
    isThirdPartyRating: algoliaEventGetters.getIsThirdPartyRating(item),
    regularPrice: algoliaEventGetters.getRegularPriceAmount(item),
    specialPrice: algoliaEventGetters.getSpecialPriceAmount(item),
    priceSuffix: algoliaEventGetters.getIsGroupPricing(item)
      ? t('product.card.price.groupPricingSuffix', {
          count: item.min_number_of_people,
        })
      : undefined,
    showAddToCartButton: false,
    style: { '--index': index },
    title: item.title || 'simple-event-showcase',
    alt: algoliaEventGetters.getPrincipalAlt(item),
    wishlistIcon: true,
    isOnWishlist: isAlgoliaEventItemOnWishlist(item),
    hasShippingCosts: algoliaEventGetters.hasShippingCosts(item),
    isDurationVisible: props.isDurationVisible,
    isTeamevent: algoliaEventGetters.getIsTeamevent(item),
    isMobileView: props.isSingleColumn,
    isBoxEvent: algoliaEventGetters.isBoxEvent(item),
    class: {
      'md:hidden': index + 1 > 4 && isCollapsed.value,
    },
    openInNewTab: !isMobile,
    qid: props.queryId,
  }
}

// Special giftcard props
const productGiftcardProps = (item: AlgoliaEventItem) => {
  return {
    dataTestId: 'product-card-giftcard',
    duration: fnGetFormattedDuration(algoliaEventGetters.fnGetDuration(item)),
    image: algoliaEventGetters.getPrincipalImage(item),
    link: `/giftcard/${algoliaEventGetters.fnGetPermalink(item)}/`,
    productCategory: algoliaEventGetters.fnGetMainCategory(item) || undefined,
    regularPrice: algoliaEventGetters.getRegularPriceAmount(item),
    specialPrice: algoliaEventGetters.getSpecialPriceAmount(item),
    rating: algoliaEventGetters.getAverageRating(item, localeBCP47),
    isPartnerRating: algoliaEventGetters.getIsPartnerRating(item),
    isThirdPartyRating: algoliaEventGetters.getIsThirdPartyRating(item),
    title: item.title || 'No title',
    wishlistIcon: true,
    isOnWishlist: isAlgoliaEventItemOnWishlist(item),
    isDurationVisible: false,
    isMobileView: props.isSingleColumn,
    openInNewTab: !isMobile,
  }
}

const fnGetFormattedDuration = (d: any) => useGetFormattedDuration(d)

const shouldRunSendViewItemList = ref(false)
const localeBCP47 = convertLocaleIntoBCP47(locale.value)

const isCollapsed = ref(props.isDesktopAccordeon)

const productShowcase = ref<HTMLElement | null>(null)

// Intersection observer for viewport-based tracking
const { intersectionHappened } = useIntersectionObserver(
  productShowcase as Ref<HTMLDivElement | undefined>
)
const hasTrackedView = ref(false)

const handleClickWishlist = (item: AlgoliaEventItem) => {
  if (isAlgoliaEventItemOnWishlist(item)) {
    removeAlgoliaEventItem(item)
  } else {
    addAlgoliaEventItem(item)
    toggleWishlistSidebar()
  }
}

const fnHandleClickOnCard = (item: AlgoliaEventItem, i: number) => {
  if (!props.disableSendEventToAlgolia) {
    useSendEventToAlgolia({
      objectID: item.objectID,
      position: Number(i + 1),
      algoliaInsightsClient: props.algoliaInsightsClient,
      label: props.algoliaInsightsLabel,
      queryId: Array.isArray(props.queryId)
        ? props.queryId.join(',')
        : props.queryId,
      locale: locale.value,
      userToken: userToken.value || '',
    })
  }

  emit('click:product-card', item)
  sendSelectListItem(props.gtmItemListId as GtmItemListId, [item], i)
}

watch(
  [() => props.products, intersectionHappened],
  ([newProducts, hasIntersected]) => {
    // Only proceed if we have products, the component has intersected with viewport, and we haven't tracked yet
    if (
      !newProducts ||
      newProducts.length === 0 ||
      !hasIntersected ||
      hasTrackedView.value
    ) {
      return
    }

    // Send GTM view_item_list event
    if (props.gtmItemListId) {
      if (import.meta.client) {
        sendViewItemList(props.gtmItemListId as GtmItemListId, props.products)
      } else {
        shouldRunSendViewItemList.value = true
      }
    }

    // Send Algolia viewedObjectIDs event
    if (props.algoliaInsightsLabel && import.meta.client) {
      sendViewedObjectIDs(
        props.products,
        props.algoliaInsightsLabel,
        props.algoliaIndexName
      )
    }

    // Mark as tracked to prevent duplicate events
    hasTrackedView.value = true
  },
  { immediate: false }
)
</script>

<style lang="postcss">
.show-more-button {
  @apply w-[300px] h-[45px] self-center mt-4 md:mt-10 hidden md:block;
}

.product-showcase-cards-container {
  @apply grid grid-cols-1 gap-2 pt-1;

  &:not(&--column) {
    @apply md:grid-cols-4 md:gap-4;
  }
}

.slider-item {
  @media (min-width: 0px) and (max-width: 320px) {
    width: 85%;
  }

  width: 70%;
  max-width: 240px;
}

.fade-slide-up-enter-active,
.fade-slide-up-leave-active {
  transition: transform 0.3s ease;
}

.fade-slide-up-enter-from,
.fade-slide-up-leave-to {
  transform: translateY(30px);
  opacity: 0;
}

.fade-slide-up-leave-active {
  position: absolute;
}
</style>
